'use client';

/** AstorQuickAskPopover — topbar quick-ask popover. Visual spec: shell.jsx `AstorQuickPopover` (271–326). Mint conventions mirror BriefViewPopover. */

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ComponentType, KeyboardEvent, ReactNode } from 'react';
import { useId, useState } from 'react';
import { useI18n } from '../../hooks';
import { GlassButton } from '../GlassButton';
import { GlassChip } from '../GlassChip';
import { GlassIconButton } from '../GlassIconButton';
import { GlassTextField } from '../GlassTextField';

export type AstorQuickAskIcon = 'doc' | 'chart' | 'flag' | 'send' | 'sparkle' | 'search' | 'bolt';

export interface AstorQuickAskSuggestion {
  id: string;
  icon: AstorQuickAskIcon;
  /** Pre-localized at the caller. */
  text: string;
  /** Undefined → inert `<div role="listitem">`, NOT a button. */
  onClick?: () => void;
}

export interface AstorQuickAskPopoverProps {
  modelName: string;
  /** Defaults to `t('astorQuickAsk.context')`. */
  contextLabel?: string;
  /** False → chip becomes `outlined` + no dot. Default true. */
  isOnline?: boolean;
  /** 0–8 items. Empty → `emptyState`. */
  suggestions: AstorQuickAskSuggestion[];
  /** Omit footer when undefined. */
  recentConversationCount?: number;
  /** Controlled value (with `onInputChange`); otherwise uncontrolled. */
  inputValue?: string;
  onInputChange?: (value: string) => void;
  /** Fires on send + Enter. Absent → send button disabled. */
  onSubmit?: (prompt: string) => void;
  /** Absent → footer ghost button hidden. */
  onOpenAstor?: () => void;
  emptyState?: ReactNode;
  sx?: SxProps<Theme>;
}

const MONO = 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace';
const ELLIPSIS = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;
const HEADER_SX = {
  px: 1.75,
  py: 1.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
} as const;
const OVERLINE_SX = {
  display: 'block',
  fontFamily: MONO,
  color: 'text.disabled',
  letterSpacing: '0.08em',
  fontWeight: 600,
  mb: 0.75,
} as const;
const FOOTER_SX = {
  mt: 1.25,
  pt: 1.25,
  borderTop: '1px solid',
  borderColor: 'divider',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  minHeight: 32,
} as const;
const HEADER_TILE_BASE = {
  width: 22,
  height: 22,
  borderRadius: '6px',
  border: '1px solid',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const;
const CHIP_DOT_BEFORE = {
  content: '""',
  position: 'absolute',
  left: 4,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: 'success.main',
} as const;

const ICON_MAP: Record<AstorQuickAskIcon, ComponentType<{ sx?: SxProps<Theme>; 'aria-hidden'?: boolean }>> = {
  doc: DescriptionOutlinedIcon,
  chart: BarChartOutlinedIcon,
  flag: FlagOutlinedIcon,
  send: SendOutlinedIcon,
  sparkle: AutoAwesomeOutlinedIcon,
  search: SearchOutlinedIcon,
  bolt: BoltOutlinedIcon,
};

export function AstorQuickAskPopover({
  modelName,
  contextLabel,
  isOnline = true,
  suggestions,
  recentConversationCount,
  inputValue,
  onInputChange,
  onSubmit,
  onOpenAstor,
  emptyState,
  sx,
}: AstorQuickAskPopoverProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const isDark = theme.palette.mode === 'dark';
  const titleId = useId();

  const mint = theme.palette.success.main;
  const mintBorder = alpha(mint, isDark ? 0.22 : 0.18);
  const mintInsetGlow = alpha(mint, isDark ? 0.06 : 0.05);
  const mintDim = alpha(mint, isDark ? 0.18 : 0.14);
  // Gate controlled mode on BOTH props so callers can't half-control it.
  const isControlled = inputValue !== undefined && onInputChange !== undefined;
  const [internalValue, setInternalValue] = useState('');
  const value = isControlled ? inputValue : internalValue;
  const submitDisabled = !onSubmit || value.trim().length === 0;
  const handleChange = (next: string) => (isControlled ? onInputChange?.(next) : setInternalValue(next));
  const handleSubmit = () => {
    const trimmed = value.trim();
    if (onSubmit && trimmed.length > 0) onSubmit(trimmed);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };
  const resolvedContextLabel = contextLabel ?? t('astorQuickAsk.context');
  const onlineLabel = t('astorQuickAsk.online');
  const showFooter = recentConversationCount !== undefined || !!onOpenAstor;

  return (
    <Paper
      role="dialog"
      aria-labelledby={titleId}
      elevation={0}
      sx={[
        {
          width: 380,
          maxWidth: '90vw',
          borderRadius: '11px',
          overflow: 'hidden',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: mintBorder,
          boxShadow: (theme: Theme) => `${theme.shadows[8]}, inset 0 0 0 1px ${mintInsetGlow}`,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box sx={HEADER_SX}>
        <Box aria-hidden sx={{ ...HEADER_TILE_BASE, backgroundColor: mintDim, borderColor: mintBorder }}>
          <AutoAwesomeOutlinedIcon sx={{ fontSize: 14, color: 'success.main' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            id={titleId}
            variant="subtitle2"
            sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2, ...ELLIPSIS }}
          >
            {t('astorQuickAsk.title')}
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: 'block', fontFamily: MONO, color: 'text.secondary', lineHeight: 1.2, ...ELLIPSIS }}
          >
            {modelName} · {resolvedContextLabel}
          </Typography>
        </Box>
        <GlassChip
          label={onlineLabel}
          size="small"
          color="success"
          variant={isOnline ? 'filled' : 'outlined'}
          aria-label={onlineLabel}
          sx={{
            height: 18,
            fontSize: '0.75rem',
            flexShrink: 0,
            ...(isOnline && {
              '& .MuiChip-label': { position: 'relative', paddingLeft: '14px' },
              '& .MuiChip-label::before': { ...CHIP_DOT_BEFORE, boxShadow: `0 0 6px ${alpha(mint, 0.6)}` },
            }),
          }}
        />
      </Box>

      <Box sx={{ p: 1.75 }}>
        <GlassTextField
          fullWidth
          size="small"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('astorQuickAsk.placeholder')}
          aria-label={t('astorQuickAsk.placeholder')}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <AutoAwesomeOutlinedIcon aria-hidden sx={{ fontSize: 14, color: 'success.main' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <GlassIconButton
                    size="xs"
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    aria-disabled={submitDisabled}
                    aria-label={t('astorQuickAsk.title')}
                    sx={{
                      width: 22,
                      height: 22,
                      backgroundColor: mintDim,
                      border: '1px solid',
                      borderColor: mintBorder,
                      color: 'success.main',
                      '&:hover': { backgroundColor: alpha(mint, isDark ? 0.26 : 0.22) },
                      '&.Mui-disabled': { opacity: 0.4, color: 'success.main' },
                    }}
                  >
                    <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                  </GlassIconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 1.5 }}
        />

        <Typography variant="overline" sx={OVERLINE_SX}>
          {t('astorQuickAsk.suggestionsHeader')}
        </Typography>

        {suggestions.length === 0 ? (
          <Box sx={{ py: 1.5, textAlign: 'center' }}>
            {emptyState ?? (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {t('astorQuickAsk.empty')}
              </Typography>
            )}
          </Box>
        ) : (
          <Box role="list" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {suggestions.map((s) => (
              <SuggestionRow key={s.id} suggestion={s} />
            ))}
          </Box>
        )}

        {showFooter && (
          <Box sx={FOOTER_SX}>
            {recentConversationCount !== undefined ? (
              <Typography variant="caption" sx={{ fontFamily: MONO, color: 'text.disabled' }}>
                {t('astorQuickAsk.recentConversations', { count: recentConversationCount })}
              </Typography>
            ) : (
              <Box />
            )}
            {onOpenAstor && (
              <GlassButton variant="ghost" size="small" onClick={onOpenAstor} sx={{ minHeight: 32 }}>
                {t('astorQuickAsk.openAstor')} →
              </GlassButton>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

function SuggestionRow({ suggestion }: { suggestion: AstorQuickAskSuggestion }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isClickable = !!suggestion.onClick;
  const IconComp = ICON_MAP[suggestion.icon];
  const mint = theme.palette.success.main;
  const hoverBg = alpha(mint, isDark ? 0.08 : 0.06);

  return (
    <Box
      // <button> only when clickable — a focusable no-op is a SR/keyboard trap.
      {...(isClickable
        ? { component: 'button' as const, type: 'button' as const, onClick: suggestion.onClick }
        : { component: 'div' as const, role: 'listitem' as const })}
      sx={{
        width: '100%',
        px: 1.25,
        py: 1,
        borderRadius: '6px',
        backgroundColor: 'transparent',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 1.125,
        textAlign: 'left',
        cursor: isClickable ? 'pointer' : 'default',
        fontFamily: 'inherit',
        color: 'text.secondary',
        transition: 'background-color 120ms ease, color 120ms ease',
        minHeight: 36,
        '&:hover': isClickable ? { backgroundColor: hoverBg, color: 'text.primary' } : undefined,
        '&:focus-visible': { outline: `2px solid ${mint}`, outlineOffset: -2 },
      }}
    >
      <IconComp aria-hidden sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }} />
      <Typography variant="body2" component="span" sx={{ flex: 1, color: 'inherit', lineHeight: 1.4, ...ELLIPSIS }}>
        {suggestion.text}
      </Typography>
      {isClickable && <ChevronRightIcon aria-hidden sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />}
    </Box>
  );
}

AstorQuickAskPopover.displayName = 'AstorQuickAskPopover';
